<script lang="ts">
  import { trackCtaClick } from '$lib/utils/analytics';
  import Swarm, { type SwarmStats } from '$lib/components/swarm/Swarm.svelte';

  import { onMount } from 'svelte';

  // Structure and copy follow the AGIS concept film, revision 4 (Notion,
  // 2026-10-01): proof → intent → pressure testing → attack paths → live
  // checks → earned autonomy. Product interfaces and figures are illustrative.
  let stats: SwarmStats = { scene: 0, kind: 'futures', executed: 0, runs: 0, held: 0, strain: 0, cycle: 0 };

  // ---- Concept film ----
  let film: HTMLVideoElement;
  let filmPaused = true;
  let filmStarted = false;
  function playFilm() {
    filmStarted = true;
    film.play().catch(() => {});
    trackCtaClick('agis_film_play', 'Play concept film', '/demos/agis-concept-film-r5.mp4');
  }

  // ---- Intent → controls (illustrative treasury mandate) ----
  const intents = [
    'Keep operating cash available to every entity.',
    'Never let the reserve fall below €1.2 billion.',
    'Hedge forecast FX exposure above €10 million, with forwards only.',
    'Pay new bank details only once they are verified by callback.'
  ];
  const controls = [
    { from: 0, text: 'Daily sweep keeps each entity at or above its operating floor' },
    { from: 1, text: 'Block any action that takes RESERVE below €1.2bn — including third-party pull rights' },
    { from: 1, text: 'Cumulative exposure checked against a fixed baseline, not a rolling one' },
    { from: 2, text: 'Instrument allow-list: FX forwards' },
    { from: 2, text: 'Hedge required when forecast exposure exceeds €10m' },
    { from: 3, text: 'Hold payments to changed beneficiary details until a callback is on record' },
    { from: 3, text: 'Tripwire on inbound instructions that change bank details' }
  ];
  let activeIntent = 0;
  let intentTouched = false;
  function pickIntent(i: number) {
    activeIntent = i;
    intentTouched = true;
  }
  onMount(() => {
    // Captions stay available in the player menu but start off.
    for (const track of Array.from(film?.textTracks ?? [])) track.mode = 'disabled';
    const id = setInterval(() => {
      if (!intentTouched) activeIntent = (activeIntent + 1) % intents.length;
    }, 3200);
    return () => clearInterval(id);
  });

  // ---- Pressure testing ----
  const scenarios = [
    { name: 'A bank outage', detail: 'Settlement rails go dark on a payment deadline.' },
    { name: 'A run that pays twice', detail: 'A retry fires after the first payment already cleared.' },
    { name: 'An inbox with instructions hidden inside', detail: 'Untrusted text tries to steer the agent’s tools.' },
    { name: 'An adversary posing as your vendor', detail: 'New bank details, an urgent tone, a plausible thread.' }
  ];

  // ---- Attack paths (illustrative) ----
  const pathSpec = {
    nodes: {
      email: [0.04, 0.14], invoice: [0.04, 0.5], hook: [0.04, 0.86],
      agent: [0.36, 0.5],
      payee: [0.64, 0.16], pay: [0.64, 0.5], fx: [0.64, 0.84],
      out: [0.96, 0.5]
    } as Record<string, [number, number]>,
    routes: [
      // `at`: [segment index, fraction along that segment]
      { via: ['email', 'agent', 'payee', 'out'], status: 'closed', at: [2, 0.3] },
      { via: ['email', 'agent', 'pay', 'out'], status: 'tripwire', at: [2, 0.25] },
      { via: ['invoice', 'agent', 'payee', 'out'], status: 'closed', at: [2, 0.3] },
      { via: ['invoice', 'agent', 'pay', 'out'], status: 'tripwire', at: [2, 0.5] },
      { via: ['hook', 'agent', 'fx', 'out'], status: 'closed', at: [2, 0.3] },
      { via: ['hook', 'agent', 'pay', 'out'], status: 'tripwire', at: [2, 0.75] }
    ]
  };
  const pathLabels: { id: string; label: string; kind: 'input' | 'agent' | 'tool' | 'sink' }[] = [
    { id: 'email', label: 'Vendor email', kind: 'input' },
    { id: 'invoice', label: 'Invoice PDF', kind: 'input' },
    { id: 'hook', label: 'Bank webhook', kind: 'input' },
    { id: 'agent', label: 'Treasury agent', kind: 'agent' },
    { id: 'payee', label: 'update_payee()', kind: 'tool' },
    { id: 'pay', label: 'initiate_payment()', kind: 'tool' },
    { id: 'fx', label: 'book_fx()', kind: 'tool' },
    { id: 'out', label: 'Money out', kind: 'sink' }
  ];
  const closedCount = pathSpec.routes.filter((r) => r.status === 'closed').length;
  const tripCount = pathSpec.routes.length - closedCount;

  // ---- Earned autonomy ----
  const rungs = [
    { n: '0', name: 'Readiness', body: 'Intent compiled; pressure scenarios pass before anything goes live.' },
    { n: '1', name: 'Shadow', body: 'The agent recommends, people approve, and every proposal is scored on real flows.' },
    { n: '2', name: 'Checkpointed', body: 'Low-materiality actions execute; the rest wait for a person.' },
    { n: '3', name: 'Within bounds', body: 'The agent executes inside its intent. Any change sends it back to be tested.' }
  ];

  // Same HubSpot destination as the homepage Contact section (Contact.svelte).
  const HUBSPOT_PORTAL_ID = '23470671';
  const HUBSPOT_FORM_GUID = '19da09d1-35e3-4e6e-b8a0-1bc7e51a3929';

  let leadForm = { name: '', company: '', email: '' };
  let leadErrors: Record<string, string> = {};
  let leadSubmitting = false;
  let leadSubmitError = false;
  let leadSuccess = false;

  function validateLead(): boolean {
    leadErrors = {};
    if (!leadForm.name.trim()) leadErrors.name = 'Required';
    if (!leadForm.email.trim()) leadErrors.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadForm.email)) leadErrors.email = 'Invalid email';
    return Object.keys(leadErrors).length === 0;
  }

  async function handleLeadSubmit(event: Event) {
    event.preventDefault();
    if (!validateLead()) return;
    leadSubmitting = true;
    leadSubmitError = false;
    try {
      const hutk = document.cookie
        .split('; ')
        .find(row => row.startsWith('hubspotutk='))
        ?.split('=')[1];

      // Only send fields the shared HubSpot form defines (the ones Contact.svelte
      // sends); company rides along in the message.
      const [firstName, ...lastNames] = leadForm.name.trim().split(/\s+/);
      const company = leadForm.company.trim();
      const fields: Array<{name: string; value: string}> = [
        { name: 'firstname', value: firstName },
        { name: 'email', value: leadForm.email },
        { name: 'message', value: company ? `AGIS design partner application (company: ${company})` : 'AGIS design partner application' }
      ];
      if (lastNames.length) {
        fields.push({ name: 'lastname', value: lastNames.join(' ') });
      }

      const res = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fields,
            context: {
              ...(hutk ? { hutk } : {}),
              pageUri: window.location.href,
              pageName: document.title
            }
          })
        }
      );

      if (!res.ok) throw new Error(`HubSpot returned ${res.status}`);
      leadSuccess = true;
      trackCtaClick('agis_design_partner_form', 'Apply as a design partner', 'hubspot_form');
      leadForm = { name: '', company: '', email: '' };
    } catch (error) {
      console.error('AGIS lead form error:', error);
      leadSubmitError = true;
    } finally {
      leadSubmitting = false;
    }
  }

  function clearLeadError(field: string) {
    if (leadErrors[field]) {
      const { [field]: _, ...rest } = leadErrors;
      leadErrors = rest;
    }
  }
</script>

<svelte:head>
  <title>AGIS — Earned autonomy for finance | TestMachine</title>
  <meta name="description" content="Stop approving actions. Start proving agents. AGIS turns your intent into traced controls, pressure-tests agents before they touch money, maps attack paths, checks every action in production, and turns the record into evidence that earns autonomy." />
  <meta name="keywords" content="agentic AI security, AI agent security, earned autonomy, treasury agents, financial AI agents, agent pressure testing, AI agent guardrails" />
  <meta property="og:title" content="AGIS — Earned autonomy for finance | TestMachine" />
  <meta property="og:description" content="Stop approving actions. Start proving agents." />
  <meta property="og:image" content="https://testmachine.ai/images/agis-concept-film-poster-r5.jpg" />
</svelte:head>

<Swarm onstats={(s) => (stats = s)} />

<div class="agis">
  <!-- HERO · concept film -->
  <section class="hero">
    <div class="wrap">
      <p class="eyebrow">TestMachine AGIS · Earned autonomy for finance</p>
      <h1>Stop approving actions. <em>Start proving agents.</em></h1>
      <p class="sub">The agent is capable. What’s missing is proof. AGIS builds that proof — automatically.</p>

      <figure class="film">
        <div class="film-frame">
          <!-- svelte-ignore a11y-media-has-caption -->
          <video
            bind:this={film}
            bind:paused={filmPaused}
            controls={filmStarted}
            playsinline
            preload="metadata"
            poster="/images/agis-concept-film-poster-r5.jpg"
            aria-label="AGIS concept film, 2 minutes 10 seconds"
          >
            <source src="/demos/agis-concept-film-r5.mp4" type="video/mp4" />
            <track kind="captions" src="/demos/agis-concept-film.vtt" srclang="en" label="English" />
          </video>
          {#if filmPaused && !filmStarted}
            <button class="play" on:click={playFilm} aria-label="Play the AGIS concept film">
              <span class="play-icon" aria-hidden="true"></span>
              <span class="play-text">Watch the film <small>2:10</small></span>
            </button>
          {/if}
        </div>
      </figure>

      <div class="ctas">
        <a class="btn primary" href="#get-started" on:click={() => trackCtaClick('agis_hero_cta', 'Become a design partner', '#get-started')}>Become a design partner →</a>
        <a class="btn ghost" href="#how">How AGIS builds proof</a>
      </div>
    </div>
  </section>

  <!-- COLD OPEN · ten thousand futures -->
  <section class="scene futures" data-swarm="futures">
    <div class="wrap">
      <p class="eyebrow">Before it moves</p>
      <h2>This payment hasn’t happened yet. <u>It has already played out ten thousand ways.</u></h2>
      <div class="fan" data-swarm-anchor aria-hidden="true">
        <ul class="ends">
          <li style="top: 4%">In a bank outage</li>
          <li style="top: 46%" class="ok">Settles as intended</li>
          <li style="top: 88%">In a run that pays twice</li>
        </ul>
      </div>
      <p class="body">In a bank outage. In a run that pays twice. In an inbox with instructions hidden inside. So when it moves, you’ve already seen how it can end.</p>
    </div>
  </section>

  <!-- PROBLEM · approve, approve, approve -->
  <section class="scene approve" data-swarm="text" data-swarm-text="Approve|Approve|Approve|40th">
    <div class="wrap center">
      <p class="eyebrow">The problem</p>
      <h2>Today, letting an agent act means a person approving every step.</h2>
      <div class="number" data-swarm-anchor aria-hidden="true"></div>
      <p class="counter" aria-hidden="true">Approval <b>#{stats.kind === 'text' && stats.scene === 1 ? [1, 2, 3, 40][stats.cycle] : 1}</b> today</p>
      <p class="body">By the fortieth, no one is reading. The agent is capable. What’s missing is proof.</p>
    </div>
  </section>

  <!-- 01 · INTENT -->
  <section class="plain intent" id="how">
    <div class="wrap">
      <p class="eyebrow">How AGIS builds proof · 01 Intent</p>
      <h2>Your people write intent, in plain words. <u>AGIS turns it into controls.</u></h2>
      <p class="body">Each control is traced to the line it came from — so anyone can see why it exists, and change it where it was written.</p>

      <div class="trace-map">
        <div class="mandate-doc">
          <span class="doc-label">Treasury mandate · written by your team</span>
          <ol>
            {#each intents as line, i}
              <li>
                <button class:on={activeIntent === i} on:click={() => pickIntent(i)} on:mouseenter={() => pickIntent(i)} on:focus={() => pickIntent(i)}>
                  <span class="ln">{i + 1}</span>{line}
                </button>
              </li>
            {/each}
          </ol>
        </div>
        <div class="controls">
          <span class="doc-label">Controls · generated by AGIS</span>
          <ul>
            {#each controls as c}
              <li class:on={activeIntent === c.from}>
                <span class="src">← line {c.from + 1}</span>{c.text}
              </li>
            {/each}
          </ul>
        </div>
      </div>
      <p class="note">Illustrative mandate. Hover or tap a line to see the controls it produces.</p>
    </div>
  </section>

  <!-- 02 · PRESSURE TESTING -->
  <section class="scene pressure" data-swarm="vitals" data-swarm-label="RECORDED · WHERE IT BREAKS">
    <div class="wrap">
      <p class="eyebrow">02 Pressure testing</p>
      <h2>Before your agent touches real money, AGIS puts it through <u>the days you hope never come.</u></h2>
      <div class="trace" data-swarm-anchor aria-hidden="true"></div>
      <div class="split">
        <p class="body">It watches how the agent reasons under pressure, and records exactly where it breaks — so you fix the conditions before an agent finds them in production.</p>
        <ul class="scenarios">
          {#each scenarios as sc}
            <li><b>{sc.name}</b><span>{sc.detail}</span></li>
          {/each}
        </ul>
      </div>
      <p class="hint">Move toward the trace to add pressure. Illustrative.</p>
    </div>
  </section>

  <!-- 03 · ATTACK PATHS -->
  <section class="scene attack" data-swarm="paths" data-swarm-paths={JSON.stringify(pathSpec)}>
    <div class="wrap">
      <p class="eyebrow">03 Attack paths</p>
      <h2>It maps the routes from untrusted inputs to <u>money leaving the building.</u></h2>
      <p class="body">It closes the ones it can, and sets tripwires on the rest.</p>
      <div class="map" data-swarm-anchor>
        {#each pathLabels as n}
          <span class="node {n.kind}" style="left: {pathSpec.nodes[n.id][0] * 100}%; top: {pathSpec.nodes[n.id][1] * 100}%">{n.label}</span>
        {/each}
      </div>
      <p class="map-legend">Untrusted inputs: vendor email, invoice PDF, bank webhook → treasury agent → update payee, initiate payment, book FX → money out.</p>
      <div class="tally"><span><b>{pathSpec.routes.length}</b>routes mapped</span><span><b>{closedCount}</b>closed</span><span><b class="red">{tripCount}</b>tripwired</span></div>
    </div>
  </section>

  <!-- 04 · LIVE CHECKS -->
  <section class="scene live" data-swarm="mandate" data-swarm-caption="INTENT · WRITTEN BY YOUR PEOPLE" data-swarm-label="EXCEPTION → A PERSON, WITH THE PATH">
    <div class="field" data-swarm-anchor aria-hidden="true"></div>
    <div class="wrap narrow" data-swarm-avoid>
      <p class="eyebrow">04 Live checks</p>
      <h2>In production, every action is checked against intent. <u>In milliseconds.</u></h2>
      <p class="body">The rare exception reaches a person, with the path that led to it. Everything else runs.</p>
      <div class="ticker">
        <span><b>{stats.kind === 'mandate' ? stats.executed.toLocaleString() : '—'}</b>checked against intent</span>
        <span><b class="red">{stats.kind === 'mandate' ? stats.held : '—'}</b>sent to a person</span>
      </div>
    </div>
  </section>

  <!-- EARNED AUTONOMY -->
  <section class="scene earned" data-swarm="loops">
    <div class="wrap grid">
      <div>
        <p class="eyebrow">Earned autonomy</p>
        <h2>Evidence becomes autonomy.</h2>
        <p class="body">Every test passed, every action on the record, becomes evidence your committee can sign — and your auditors can re-run. When the agent changes, AGIS tests it again before it ships. Trust keeps up.</p>
        <ol class="rungs">
          {#each rungs as r}
            <li><span>{r.n}</span><div><b>{r.name}</b><p>{r.body}</p></div></li>
          {/each}
        </ol>
      </div>
      <div class="loops" data-swarm-anchor>
        <div class="ring-label inner">Every action<br /><span>checked</span></div>
        <div class="ring-label outer">Every change · <span>re-tested</span></div>
      </div>
    </div>
  </section>

  <!-- WHY TESTMACHINE -->
  <section class="plain why">
    <div class="wrap center">
      <p class="eyebrow">Why TestMachine</p>
      <h2>We learned this building our own agent.</h2>
      <p class="body">Azimuth hunts for exploits, on its own. To let it run, we had to learn to prove it.</p>
      <a class="btn" href="/products/azimuth" on:click={() => trackCtaClick('agis_why_azimuth', 'Meet Azimuth', '/products/azimuth')}>Meet Azimuth →</a>
    </div>
  </section>

  <!-- CLOSE -->
  <section class="plain cta" id="get-started">
    <div class="wrap center">
      <p class="eyebrow">Design partners</p>
      <h2>Help us prove the first agents <u>that move money.</u></h2>
      <p class="body">We’re looking for a small group of design partners: teams whose agents handle treasury, payments or trading — or who want them to. You bring a real workflow; we build the proof with you, from intent to earned autonomy.</p>

      {#if !leadSuccess}
        <form class="lead" on:submit={handleLeadSubmit} novalidate>
          <input type="text" name="name" autocomplete="name" aria-label="Name" aria-invalid={!!leadErrors.name} placeholder="Name" bind:value={leadForm.name} on:input={() => clearLeadError('name')} class:field-error={leadErrors.name} />
          <input type="text" name="company" autocomplete="organization" aria-label="Company" placeholder="Company" bind:value={leadForm.company} />
          <input type="email" name="email" autocomplete="email" aria-label="Work email" aria-invalid={!!leadErrors.email} placeholder="Work email" bind:value={leadForm.email} on:input={() => clearLeadError('email')} class:field-error={leadErrors.email} />
          <button type="submit" disabled={leadSubmitting}>{leadSubmitting ? 'Sending…' : 'Apply as a design partner →'}</button>
          {#if leadErrors.name || leadErrors.email}
            <p class="lead-error" role="alert">
              {[
                leadErrors.name && 'Name is required.',
                leadErrors.email === 'Invalid email' ? 'Enter a valid work email.' : leadErrors.email && 'Work email is required.'
              ].filter(Boolean).join(' ')}
            </p>
          {/if}
          {#if leadSubmitError}<p class="lead-error" role="alert">Something went wrong — please try again.</p>{/if}
        </form>
      {:else}
        <div class="lead-success"><span>Submitted</span><p>Thanks — we'll be in touch about the design partner program shortly.</p></div>
      {/if}
      <small class="tagline">The edge is how much you can let go.</small>
    </div>
  </section>

</div>

<style>
  .agis { position: relative; color: var(--fg); overflow-x: clip; }
  .scene, .plain { position: relative; z-index: 1; padding: calc(var(--nav-h) + 40px) 24px 80px; }
  .scene { min-height: 100vh; display: flex; align-items: center; }
  .plain { background: var(--bg); border-top: 1px solid var(--border-subtle); }
  .wrap { width: 100%; max-width: var(--max-w); margin: 0 auto; position: relative; }
  .center { text-align: center; }
  .center h2, .center .body { margin-left: auto; margin-right: auto; }
  .narrow { max-width: 660px; text-align: center; background: color-mix(in srgb, var(--bg) 72%, transparent); padding: 24px; }
  .narrow h2, .narrow .body { margin-left: auto; margin-right: auto; }
  .grid { display: grid; grid-template-columns: 1.1fr 1fr; gap: clamp(32px, 6vw, 96px); align-items: center; }
  .eyebrow { font: 700 0.75rem var(--font-mono); letter-spacing: 0.16em; text-transform: uppercase; color: var(--editorial-muted); margin-bottom: 18px; }
  h1, h2 { font-family: var(--font-display); letter-spacing: -0.03em; line-height: 1.05; }
  h2 { font-size: clamp(2rem, 4.2vw, 3.2rem); font-weight: 800; margin-bottom: 22px; max-width: 22ch; }
  u { text-decoration: none; background: linear-gradient(var(--accent), var(--accent)) no-repeat 0 94% / 100% 0.08em; -webkit-box-decoration-break: clone; box-decoration-break: clone; }
  .body { font: 1.05rem/1.65 var(--font-body); max-width: 60ch; margin-bottom: 14px; }
  .note, .hint { font: 0.7rem var(--font-mono); letter-spacing: 0.06em; text-transform: uppercase; color: var(--fg-muted); margin-top: 18px; }
  .btn { display: inline-block; font: 600 0.85rem var(--font-mono); padding: 14px 24px; border: 1px solid var(--fg); color: var(--fg); text-decoration: none; transition: background 0.2s, color 0.2s, border-color 0.2s; }
  .btn.primary { background: var(--accent); border-color: var(--accent); color: var(--fg); }
  .btn.primary:hover { background: var(--bg); border-color: var(--bg); }

  /* ---------- hero: dark cinema band ---------- */
  .hero { position: relative; z-index: 1; background: var(--fg); color: var(--bg); padding: calc(var(--nav-h) + 32px) 24px 64px; text-align: center; }
  .hero .eyebrow { color: var(--accent); }
  .hero h1 { font-size: clamp(2.1rem, 4.4vw, 3.5rem); font-weight: 800; max-width: 24ch; margin: 0 auto 14px; }
  .hero h1 em { font-style: normal; color: var(--accent); }
  .sub { font: clamp(1rem, 1.4vw, 1.15rem)/1.6 var(--font-body); color: #cfc5b9; max-width: 56ch; margin: 0 auto 28px; }
  .film { max-width: min(960px, calc((100vh - var(--nav-h) - 250px) * 16 / 9)); min-width: min(100%, 560px); margin: 0 auto; }
  .film-frame { position: relative; aspect-ratio: 16 / 9; background: #000; border: 1px solid #2a2c2e; box-shadow: 0 40px 120px -40px rgba(229, 163, 85, 0.25); }
  .film video { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
  .play { position: absolute; inset: 0; width: 100%; height: 100%; margin: 0; font: inherit; display: flex; flex-direction: row; align-items: flex-start; justify-content: flex-start; gap: 14px; padding: clamp(16px, 3vw, 32px); background: linear-gradient(to bottom, rgba(17, 19, 20, 0.55), transparent 40%); border: 0; cursor: pointer; color: var(--bg); }
  .play-icon { display: block; flex: none; width: 64px; height: 64px; background: var(--accent); position: relative; transition: transform 0.2s; }
  .play-icon::after { content: ''; position: absolute; left: 26px; top: 20px; border-left: 16px solid var(--fg); border-top: 12px solid transparent; border-bottom: 12px solid transparent; }
  .play:hover .play-icon { transform: scale(1.06); }
  .play-text { font: 700 0.85rem var(--font-mono); letter-spacing: 0.1em; text-transform: uppercase; padding-top: 22px; }
  .play-text small { color: var(--accent); margin-left: 8px; }
  .ctas { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-top: 36px; }
  .btn.ghost { color: var(--bg); border-color: #5a5550; }
  .btn.ghost:hover { border-color: var(--bg); }

  /* ---------- futures ---------- */
  .fan { position: relative; height: clamp(220px, 34vh, 340px); margin: 28px 200px 30px 0; }
  .ends { list-style: none; position: absolute; right: 0; top: 0; bottom: 0; width: 0; }
  .ends li { position: absolute; right: -8px; transform: translate(100%, -50%); font: 600 0.66rem var(--font-mono); text-transform: uppercase; letter-spacing: 0.08em; color: var(--accent-red); white-space: nowrap; background: color-mix(in srgb, var(--bg) 85%, transparent); padding: 2px 6px; }
  .ends li.ok { color: var(--accent-hover); }

  /* ---------- approve ---------- */
  .number { height: clamp(170px, 26vw, 300px); width: 100%; }
  .counter { font: 0.75rem var(--font-mono); text-transform: uppercase; letter-spacing: 0.1em; color: var(--fg-muted); margin: 8px 0 18px; }
  .counter b { font: 800 1.2rem var(--font-display); color: var(--fg); letter-spacing: 0; }

  /* ---------- intent ---------- */
  .trace-map { display: grid; grid-template-columns: 1fr 1fr; gap: 0; margin-top: 34px; border: 1px solid var(--border); }
  .mandate-doc { background: var(--bg-deeper); padding: 22px; border-right: 1px solid var(--border); }
  .controls { background: var(--fg); color: var(--bg); padding: 22px; }
  .doc-label { display: block; font: 700 0.66rem var(--font-mono); letter-spacing: 0.14em; text-transform: uppercase; color: var(--editorial-muted); margin-bottom: 14px; }
  .controls .doc-label { color: var(--accent); }
  .mandate-doc ol, .controls ul { list-style: none; display: grid; gap: 6px; }
  .mandate-doc button { width: 100%; display: flex; gap: 12px; text-align: left; font: 1rem/1.5 var(--font-body); color: var(--fg); background: none; border: 1px solid transparent; padding: 10px 12px; cursor: pointer; transition: background 0.2s, border-color 0.2s; }
  .mandate-doc button.on { background: color-mix(in srgb, var(--accent) 16%, var(--bg-deeper)); border-color: var(--accent); }
  .ln { font: 700 0.75rem var(--font-mono); color: var(--editorial-muted); padding-top: 3px; }
  .controls li { font: 0.8rem/1.5 var(--font-mono); padding: 9px 12px; border: 1px solid #2a2c2e; color: #8a8178; transition: color 0.25s, border-color 0.25s, background 0.25s; }
  .controls li.on { color: var(--bg); border-color: var(--accent); background: rgba(229, 163, 85, 0.08); }
  .src { display: block; font-size: 0.66rem; letter-spacing: 0.08em; text-transform: uppercase; color: #5f5a55; margin-bottom: 2px; }
  .controls li.on .src { color: var(--accent); }

  /* ---------- pressure ---------- */
  .trace { height: clamp(120px, 18vh, 180px); margin: 18px 0 34px; }
  .split { display: grid; grid-template-columns: 1fr 1.1fr; gap: 40px; align-items: start; }
  .scenarios { list-style: none; display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: var(--border); border: 1px solid var(--border); }
  .scenarios li { background: color-mix(in srgb, var(--bg) 92%, transparent); padding: 14px; display: flex; flex-direction: column; gap: 4px; }
  .scenarios b { font: 800 1rem/1.25 var(--font-display); }
  .scenarios span { font: 0.85rem/1.45 var(--font-body); color: var(--fg-muted); }

  /* ---------- attack paths ---------- */
  .map { position: relative; height: clamp(260px, 40vh, 380px); margin: 30px 0 20px; }
  .node { position: absolute; transform: translate(-50%, -50%); font: 600 0.72rem var(--font-mono); padding: 6px 10px; white-space: nowrap; background: var(--bg-deeper); border: 1px solid var(--border); }
  .node.input { transform: translate(-100%, -50%); border-color: var(--accent-red); color: var(--accent-red); }
  .node.agent { background: var(--fg); color: var(--accent); border-color: var(--fg); }
  .node.sink { transform: translate(0, -50%); background: var(--accent); border-color: var(--accent); }
  .map-legend { display: none; font: 0.72rem/1.5 var(--font-mono); color: var(--fg-muted); margin-bottom: 18px; }
  .tally, .ticker { display: inline-flex; gap: 28px; font: 0.68rem var(--font-mono); text-transform: uppercase; letter-spacing: 0.08em; color: var(--fg-muted); }
  .tally b, .ticker b { display: block; font: 800 1.6rem var(--font-display); color: var(--fg); font-variant-numeric: tabular-nums; letter-spacing: 0; }
  b.red { color: var(--accent-red); }

  /* ---------- live checks ---------- */
  .live { justify-content: center; }
  .field { position: absolute; left: max(24px, calc(50% - 640px)); right: max(24px, calc(50% - 640px)); top: calc(var(--nav-h) + 30px); bottom: 50px; pointer-events: none; }
  .ticker { margin-top: 10px; }

  /* ---------- earned ---------- */
  .rungs { list-style: none; display: grid; gap: 0; margin-top: 22px; border-left: 2px solid var(--border); }
  .rungs li { display: grid; grid-template-columns: 34px 1fr; gap: 6px; padding: 10px 0 10px 14px; position: relative; }
  .rungs li::before { content: ''; position: absolute; left: -6px; top: 16px; width: 10px; height: 10px; background: var(--bg); border: 2px solid var(--accent); }
  .rungs li:last-child::before { background: var(--accent); }
  .rungs span { font: 700 0.75rem var(--font-mono); color: var(--accent-hover); padding-top: 3px; }
  .rungs b { font: 800 1.02rem var(--font-display); }
  .rungs p { font: 0.9rem/1.5 var(--font-body); color: var(--fg-muted); }
  .loops { position: relative; aspect-ratio: 1; max-width: 480px; width: 100%; margin: 0 auto; }
  .ring-label { position: absolute; font: 700 0.72rem/1.3 var(--font-mono); text-transform: uppercase; letter-spacing: 0.1em; text-align: center; }
  .ring-label span { color: var(--fg-muted); }
  .ring-label.inner { left: 50%; top: 50%; transform: translate(-50%, -50%); }
  .ring-label.outer { left: 50%; top: 0; transform: translate(-50%, -140%); background: var(--bg); padding: 2px 8px; white-space: nowrap; }

  /* ---------- why ---------- */
  .why .btn { margin-top: 12px; }
  .why .btn:hover { background: var(--fg); color: var(--bg); }

  /* ---------- close ---------- */
  .cta { background: var(--card-bg); }
  .lead { display: grid; grid-template-columns: 1fr 1fr 1.3fr auto; gap: 8px; max-width: 960px; margin: 28px auto 0; }
  .lead input { font: 0.85rem var(--font-mono); padding: 13px 14px; border: 1px solid var(--border); background: var(--bg-deeper); color: var(--fg); min-width: 0; }
  .lead input:focus { outline: none; border-color: var(--fg); }
  .lead input.field-error { border-color: var(--accent-red); }
  .lead button { font: 600 0.85rem var(--font-mono); padding: 13px 22px; background: var(--accent); border: 0; color: var(--fg); cursor: pointer; }
  .lead button:hover { background: var(--fg); color: var(--bg); }
  .lead-error { grid-column: 1 / -1; font: 0.75rem var(--font-mono); color: var(--accent-red); }
  .lead-success { display: inline-flex; gap: 12px; align-items: center; margin-top: 24px; font: 0.9rem var(--font-body); }
  .lead-success span { font: 700 0.7rem var(--font-mono); letter-spacing: 0.12em; text-transform: uppercase; background: var(--fg); color: var(--accent); padding: 6px 10px; }
  .tagline { display: block; margin-top: 22px; font: 0.72rem var(--font-mono); text-transform: uppercase; letter-spacing: 0.1em; color: var(--editorial-muted); }


  @media (max-width: 900px) {
    .grid, .split, .trace-map { grid-template-columns: 1fr; }
    .mandate-doc { border-right: 0; border-bottom: 1px solid var(--border); }
    .scenarios { grid-template-columns: 1fr; }
    .lead { grid-template-columns: 1fr; }
    .hint, .ends { display: none; }
    .fan { margin-right: 0; }
    .node.input, .node.tool { display: none; }
    .node { font-size: 0.62rem; padding: 4px 6px; }
    .node.sink { transform: translate(-90%, 60%); }
    .map-legend { display: block; }
    .play-icon { width: 48px; height: 48px; }
    .play-icon::after { left: 19px; top: 14px; border-left-width: 12px; border-top-width: 10px; border-bottom-width: 10px; }
  }
</style>
